import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Grid from '@mui/material/Grid2';
import { useSnackbar } from 'notistack';
import InfiniteScroll from 'react-infinite-scroll-component';
import { defaultVariables } from '../../../../config';
import VendorsListSkeleton from "../../../components/loaders/VendorsListSkeleton";
import { CircularProgress } from "@mui/material";
import {ChevronLeft, PackageOpen} from "lucide-react";
import Button from "@mui/material/Button";
import {Link} from "react-router-dom";
import NothingFound from "../../../components/elements/NothingFound";
import {useTranslation} from "react-i18next";
import {addressQueryStringId} from "../../../../config/values";
import SingleCardProductHome from "../products/components/SingleCardProductHome";

function ProductsWithFilters({ industry, filters, search = '', setTotal }) {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const [products, setProducts] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [initialLoad, setInitialLoad] = useState(true);

    const fetchProducts = async (pageToLoad = 1) => {
        setLoading(true);
        const config = {
            headers: {
                Accept: 'application/json',
            },
            params: {
                page: pageToLoad,
                filters,
                address_id: addressQueryStringId(),
                search
            },
        };
        try {
            const response = await axios.get(`${defaultVariables.url}/front/products-with-filter/${industry}`, config);
            const newProducts = response.data.data;
            if (!!setTotal) setTotal(response.data.total);
            if (pageToLoad > 1) {
                setProducts((prevProducts) => {
                    const merged = [...prevProducts, ...newProducts];
                    return merged.filter((v, i, self) =>
                        i === self.findIndex((t) => t.id === v.id)
                    );
                });
            } else {
                setProducts(newProducts);
            }
            setHasMore(newProducts.length > 0);
        } catch (error) {
            console.error('Error fetching products:', error);
            enqueueSnackbar('Error fetching products. Please try again.', { variant: 'error' });
        } finally {
            setLoading(false);
            setInitialLoad(false);
        }
    };

    useEffect(() => {
        setProducts([]);
        setPage(1);
        setHasMore(true);
        setInitialLoad(true);
        fetchProducts(1);
    }, [industry, filters, search]);

    const loadMore = () => {
        if (!loading && hasMore) {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchProducts(nextPage);
        }
    };

    return (
        <>
            {initialLoad && (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <VendorsListSkeleton />
                </div>
            )}

            {!initialLoad && products.length === 0 && !hasMore && (
                <NothingFound
                    type="page"
                    title={t('noProductFound')}
                    title_color="text"
                    paragraph={t('noProductFoundTryOther')}
                    icon={<PackageOpen/>}
                >
                    <Button
                        variant="outlined"
                        startIcon={<ChevronLeft/>}
                        component={Link}
                        to={`/${industry}`}
                    >
                        {t('back')}
                    </Button>
                </NothingFound>
            )}

            {!initialLoad && (
                <InfiniteScroll
                    dataLength={products.length}
                    next={loadMore}
                    hasMore={hasMore}
                    loader={(
                        <div style={{ textAlign: 'center', padding: '20px 0' }}>
                            {loading && <CircularProgress />}
                        </div>
                    )}
                >
                    <Grid container spacing={2}>
                        {products.map((product) => (
                            <Grid item size={{xs:12,sm:6,md:4,lg:3}} key={product.id}>
                                <SingleCardProductHome product={product}/>
                            </Grid>
                        ))}
                    </Grid>
                </InfiniteScroll>
            )}
        </>
    );
}

export default ProductsWithFilters;
